import { Avatar, Box, Button, styled, Typography } from "@mui/material"
import dompurify from "dompurify"
import useElementCountLines from "hooks/useElementCountLines"
import ConventionTeamMember from "models/convention-api/v1/ConventionTeamMember"
import React, { useRef } from "react"
import { nameToInitials, pxToRem } from "utils/helpers"

interface TeamMemberCardProps {
  teamMember: ConventionTeamMember
  onReadMore: (teamMember: ConventionTeamMember) => void
}

const MAX_NUMBER_OF_LINES = 8
const MAX_NUMBER_OF_LINES_WITH_READ_MORE = MAX_NUMBER_OF_LINES - 2

const TeamMemberCard = (props: TeamMemberCardProps) => {
  const { teamMember, onReadMore } = props

  const cardBioTextRef = useRef<HTMLParagraphElement>(null)

  const bioTextNumberOfLines = useElementCountLines(cardBioTextRef)
  const name = getTeamMemberName(teamMember)

  const bioTextLineClamp = (() => {
    if (bioTextNumberOfLines == null) return "initial"
    if (bioTextNumberOfLines === MAX_NUMBER_OF_LINES_WITH_READ_MORE) {
      return MAX_NUMBER_OF_LINES_WITH_READ_MORE
    }
    if (bioTextNumberOfLines > MAX_NUMBER_OF_LINES) return MAX_NUMBER_OF_LINES_WITH_READ_MORE
    return MAX_NUMBER_OF_LINES
  })()

  const bioTextOverflow = (() => {
    if (bioTextNumberOfLines === null) return "initial"
    if (bioTextNumberOfLines === MAX_NUMBER_OF_LINES_WITH_READ_MORE) return "hidden"

    return bioTextNumberOfLines > MAX_NUMBER_OF_LINES ? "hidden" : "initial"
  })()

  const shouldShowReadMore = bioTextLineClamp === MAX_NUMBER_OF_LINES_WITH_READ_MORE

  return (
    <_CardContainer>
      <_CardHeader>
        <_CardHeaderAvatar
          src={teamMember.admin_avatar_thumb_url ?? undefined}
          children={nameToInitials(name)}
        />
        <_CardHeaderTextContainer>
          <Typography variant="header5">{name}</Typography>
          <Typography variant="paragraphRegular">{teamMember.admin_title}</Typography>
        </_CardHeaderTextContainer>
      </_CardHeader>
      <_CardBioText
        {...{ ref: cardBioTextRef }}
        component="p"
        variant="paragraphRegular"
        style={{
          // TODO: figure out what's wrong with webkitLineClamp type
          // The value must be a string since it adds postfix `px` if it's not
          // @ts-ignore
          webkitLineClamp: `${bioTextLineClamp}`,
          overflow: bioTextOverflow,
        }}
        dangerouslySetInnerHTML={{
          __html: dompurify.sanitize(teamMember.admin_bio ?? ""),
        }}
      />
      {shouldShowReadMore && (
        <_CardReadMoreContainer>
          <Button onClick={() => onReadMore(teamMember)}>Read More</Button>
        </_CardReadMoreContainer>
      )}
    </_CardContainer>
  )
}

export default TeamMemberCard

const _CardContainer = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  display: "flex",
  flexFlow: "column nowrap",
  gap: pxToRem(15),
  height: pxToRem(270),
  width: "100%",
  minWidth: pxToRem(300),
  maxWidth: pxToRem(500),
  borderRadius: pxToRem(10),
  padding: pxToRem(15),
  background: theme.palette.gray.lightest,
  boxShadow: `0 ${pxToRem(2)} ${pxToRem(2)} rgba(0, 0, 0, 0.25), 0 0 ${pxToRem(
    2,
  )} rgba(0, 0, 0, 0.1)`,
  overflow: "hidden",
})) as typeof Box

const _CardHeader = styled(Box)(() => ({
  display: "flex",
  flexFlow: "row nowrap",
  gap: pxToRem(10),
})) as typeof Box

const _CardHeaderAvatar = styled(Avatar)(({ theme }) => ({
  width: pxToRem(46),
  height: pxToRem(46),
  backgroundColor: theme.palette.primary.main,
})) as typeof Avatar

const _CardHeaderTextContainer = styled(Box)(() => ({
  flex: 1,
  display: "flex",
  flexFlow: "column nowrap",
  gap: pxToRem(5),
})) as typeof Box

const _CardBioText = styled(
  Typography,
  {},
)(({}) => ({
  width: "100%",
  display: "-webkit-box",

  "-webkit-box-orient": "vertical",
})) as typeof Typography

const _CardReadMoreContainer = styled(Box)(() => ({
  display: "flex",
  justifyContent: "flex-end",
  width: "100%",
  marginTop: "auto",
})) as typeof Box

const getTeamMemberName = (teamMember: ConventionTeamMember) =>
  teamMember.admin_first_name + " " + teamMember.admin_last_name
